import { Box } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";

import { useJobResultsQuery } from "@/queries/query-process-results";

import { useProcessRunSchemas } from "../project-process-run/hooks/use-process-run-schemas";
import { ObjectSchema } from "../project-process-run/types/object-schema";

import { ResultDirectory } from "./components/result-directory";
import { ResultFileItem } from "./components/result-file-item";
import { ResultTable } from "./components/result-table";
import { RootResultError } from "./components/root-result-error";
import { ProcessRunResultsProps } from "./types/process-results-props";
import {
  extractDirectoryInfo,
  extractFileInfo,
  getExpectedFileCount,
} from "./utils/extract-simple-file-results";
import { groupResultProperties } from "./utils/group-result-properties";
import { propertyHasFormat } from "./utils/schema-property-utils";

export function ProcessRunResults(props: ProcessRunResultsProps) {
  return (
    <AnimateSize>
      <ProcessRunResultsInner {...props} />
    </AnimateSize>
  );
}

const schemaIsValid = (schema: unknown): schema is { properties: Record<string, ObjectSchema> } => {
  return (
    typeof schema === "object" &&
    !!schema &&
    "properties" in schema &&
    typeof schema.properties === "object" &&
    !!schema?.properties &&
    Object.keys(schema.properties).length > 0
  );
};

const NoResultsAvailable = () => (
  <Box py={4} textAlign="center" fontStyle="italic" color="brand.analytic">
    <FormattedMessage defaultMessage="No results available" id="processRunResults.noResults" />
  </Box>
);

function ProcessRunResultsInner(props: ProcessRunResultsProps) {
  const { isLoading: schemaLoading, resultOutputSchema } = useProcessRunSchemas(props);
  const jobResultsQuery = useJobResultsQuery(props.job);

  if (schemaLoading || jobResultsQuery.isPending) {
    return null;
  }

  const schema = resultOutputSchema as { properties: Record<string, ObjectSchema> };

  if (!schemaIsValid(schema)) {
    console.warn("Invalid schema provided to ProcessRunResults:", schema);
    return <NoResultsAvailable />;
  }

  // Group properties respecting ui:order
  const uiOrder = "ui:order" in schema ? (schema["ui:order"] as string[]) : undefined;
  const schemaProperties = schema.properties;
  const items = groupResultProperties(schemaProperties, uiOrder);

  if (items.length === 0) {
    console.warn("No items returned by `groupResultProperties` for schema:", schema);
    return <NoResultsAvailable />;
  }

  return items.map((item, idx) => {
    switch (item.type) {
      case "file": {
        const key = Object.keys(item.properties)[0];
        const property = item.properties[key];
        const values = jobResultsQuery.data || {};
        const value = jobResultsQuery.data?.[key];

        // Check if next item is a table to decide if we should reduce spacing
        const nextItemIsTable = idx + 1 < items.length && items[idx + 1].type === "table";

        if (!value) {
          return (
            <RootResultError propertyKey={key} property={property} values={values} key={key}>
              <FormattedMessage
                defaultMessage="Result of `{title}` is not found in the process results"
                id="processRunResults.fileNotFound"
                values={{
                  title: property.title,
                }}
              />
            </RootResultError>
          );
        }

        // Extract file info
        if (propertyHasFormat(property, "rana_file")) {
          const fileInfo = extractFileInfo(key, value);
          if (fileInfo) {
            return (
              <ResultFileItem
                key={key}
                fileId={fileInfo.fileId}
                fileRef={fileInfo.fileRef}
                reduced={nextItemIsTable}
              />
            );
          }
        }

        // Extract directory info
        if (propertyHasFormat(property, "rana_directory")) {
          const dirInfo = extractDirectoryInfo(key, value);
          if (dirInfo) {
            return (
              <ResultDirectory
                key={key}
                job={props.job}
                directoryId={dirInfo.directoryId}
                expectedAmount={getExpectedFileCount(property)}
                reduced={nextItemIsTable}
              />
            );
          }
        }

        return (
          <RootResultError propertyKey={key} property={property} values={values} key={key}>
            <FormattedMessage
              defaultMessage="Result of `{title}` has an unsupported format and cannot be displayed"
              id="processRunResults.fileUnsupportedFormat"
              values={{
                title: property.title,
              }}
            />
          </RootResultError>
        );
      }

      case "table":
        return (
          <ResultTable
            key={Object.keys(item.properties).join(",")}
            properties={item.properties}
            values={jobResultsQuery.data || {}}
            isLoading={jobResultsQuery.isPending}
          />
        );

      default: {
        const exhaustiveCheck: never = item.type;
        return exhaustiveCheck;
      }
    }
  });
}
